if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer') || document.getElementById('rebuy-cart');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.handleAddonChange = this.handleAddonChange.bind(this);
        this.quantities = {};

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      connectedCallback() {
        this.unsubscribeAddonChange = subscribe(PUB_SUB_EVENTS.addonChange, this.handleAddonChange);
        
      }

      openSmartCartWhenReady() {
        const openSmartCart = () => {
          try {
            if (window.Rebuy?.SmartCart && typeof window.Rebuy.SmartCart.show === 'function') {
              window.Rebuy.SmartCart.show();
              return true;
            }
          } catch (error) {
            console.warn('Rebuy Smart Cart is not ready yet.', error);
          }

          return false;
        };

        // Rebuy may still be loading when a customer clicks Add to bag for the
        // first time. The item has already been added at this point, so never
        // submit it again; wait for Smart Cart and open that same cart instead.
        if (openSmartCart()) return Promise.resolve(true);

        return new Promise((resolve) => {
          let attempts = 0;
          const maxAttempts = 50;
          let rebuyReadyTimer;

          const stopWaiting = (opened) => {
            document.removeEventListener('rebuy:cart.ready', handleRebuyReady);
            window.clearInterval(rebuyReadyTimer);
            resolve(opened);
          };

          const handleRebuyReady = () => {
            if (openSmartCart()) stopWaiting(true);
          };

          document.addEventListener('rebuy:cart.ready', handleRebuyReady);

          rebuyReadyTimer = window.setInterval(() => {
            attempts += 1;

            if (openSmartCart()) {
              stopWaiting(true);
            } else if (attempts >= maxAttempts) {
              stopWaiting(false);
            }
          }, 100);
        });
      }

      handleAddonChange(event) {
        const variantId = event.variantId;
        const quantity = parseInt(event.quantity) || 0;
      
        // Save the quantity for each variant
        this.quantities[variantId] = quantity;
      
        // Total all addon quantities
        const totalAddonQty = Object.values(this.quantities).reduce((sum, qty) => sum + qty, 0);
      
        // Get main product quantity (outside the form)
        const mainQtyInput = document.querySelector(`input[name="quantity"][form="${this.form.id}"]`);
        
        const mainQty = parseInt(mainQtyInput?.value) || 1;
      
        if (totalAddonQty === 0) {
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        } else {
          const totalQty = mainQty + totalAddonQty;
          this.submitButtonText.textContent = `Add ${totalQty} Items To Bag`;
        }
      }
      
      

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');
        const config = {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          }
        };
        
        const formData = new FormData(this.form);
        
        const mainQuantityInput = document.querySelector(
          `input[name="quantity"][form="${this.form.id}"], select[name="quantity"][form="${this.form.id}"]`
        );
        
        const mainItem = {
          id: parseInt(formData.get('id')),
          quantity: parseInt(mainQuantityInput?.value) || 1
        };
        
        const addonItems = Array.from(document.querySelectorAll('product-addons .select__select'))
          .map(select => {
            if (select.disabled) return null;
            const id = parseInt(select.name);
            const quantity = parseInt(select.value);
            if (!id || quantity < 1) return null;
            return { id, quantity };
          })
          .filter(Boolean);
        
        const items = [mainItem, ...addonItems];
        
        config.body = JSON.stringify({ items });
        
        fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, config)
          .then(async (res) => {
            const response = await res.json();

            if (!res.ok) {
              throw new Error(response.description || response.message || 'Unable to add this item to the cart.');
            }

            return response;
          })
          .then((response) => {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: mainItem.id,
              cartData: response
            });

            // Give immediate confirmation and keep the button locked while a
            // late-loading Rebuy instance opens. This prevents a second click
            // from accidentally adding a duplicate item.
            this.submitButtonText.textContent = 'Added to bag';
            return this.openSmartCartWhenReady().then((opened) => {
              // The cart operation already succeeded. If the Rebuy app fails
              // to initialise completely, take the customer to the regular
              // Shopify cart instead of leaving the successful add invisible.
              if (!opened) {
                window.location.assign(`${window.Shopify?.routes?.root || '/'}cart`);
              }
            });
          })
          .catch((error) => {
            console.error(error);
            this.handleErrorMessage(error.message);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart?.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.submitButtonText.textContent = window.variantStrings.addToCart;
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
        
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      toggleAddonSubmitButton() {
        // If form has addon, we must edit the text to say 'Add 2 Items To Bag' for example.
        // When adding to cart, we must add the addon to the cart as well.
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
